/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/**
 * Escapes a value for use inside a single-quoted OData string literal in a URL.
 *
 * Single quotes are doubled (OData literal escaping) so that user-controlled input cannot
 * terminate the literal and inject additional function parameters or query options, and the
 * result is URL-encoded so that reserved characters (&, ?, #, %, spaces) stay inside the literal.
 * Note that encodeURIComponent intentionally leaves the doubled quotes untouched, which is what
 * OData expects.
 */
export const escapeODataStringLiteral = (value: string | number | undefined | null): string =>
    encodeURIComponent(String(value ?? '').replace(/'/g, "''"));

/**
 * Coerces a value used as an unquoted numeric OData parameter into a safe integer literal.
 * Anything that is not a finite number falls back to the given default.
 */
export const escapeODataNumberLiteral = (value: string | Number | number | undefined | null, fallback: number = 0): string => {
    const parsed = Number(value);

    return Number.isFinite(parsed) ? String(Math.trunc(parsed)) : String(fallback);
};
